import { APIAuthenticationType, AuthUserType } from "../../Actions/Types"
import { Action, Reducer } from "redux"
import {
    APIUserModel,
    calculateFreemiumPlanExpiry
} from "../../Models/API/UserModel"
import { AuthAction } from "../Auth"
import { BillingAction } from "./Billing/BillingReducer"

export interface APIAuthenticationState {
    apiKeys: string[]
    user: APIUserModel | null
    ownerExists: {
        [collaborationId: number]:
            | {
                  exists: boolean
                  email: string
              }
            | undefined
    }
    orgMemberOwnerExists: {
        [memberId: number]:
            | {
                  exists: boolean
                  email: string
              }
            | undefined
    }
    workerOwnerExists: {
        [workerId: number]:
            | {
                  exists: boolean
                  email: string
              }
            | undefined
    }
    freemiumPlanExpiryDays: number | undefined
}

interface APIAuthenticationSetAPIKeysAction {
    type: APIAuthenticationType.setAPIKeys
    payload: string[]
}

export interface APIAuthenticationSetUserInfoAction {
    type: APIAuthenticationType.setUserInfo
    payload: APIUserModel
}

export interface APIAuthenticationSetOwnerExistsAction {
    type: APIAuthenticationType.setCollaborationOwnerExists
    meta: {
        collaborationId: number
    }
    payload: {
        exists: boolean
        email: string
    }
}

export interface APIAuthenticationSetOrgMemberOwnerExistsAction {
    type: APIAuthenticationType.setOrgMemberOwnerExists
    meta: {
        memberId: number
    }
    payload: {
        exists: boolean
        email: string
    }
}

export interface APIAuthenticationSetOrgWorkerOwnerExistsAction {
    type: APIAuthenticationType.setWorkerOwnerExists
    meta: {
        workerId: number
    }
    payload: {
        exists: boolean
        email: string
    }
}

export interface APIAuthenticationSetUserPlanExpired
    extends Action<APIAuthenticationType.setFreemiumPlanExpiryDays> {
    payload?: number
}

export type APIAuthenticationAction =
    | APIAuthenticationSetAPIKeysAction
    | APIAuthenticationSetUserInfoAction
    | APIAuthenticationSetOwnerExistsAction
    | APIAuthenticationSetOrgMemberOwnerExistsAction
    | APIAuthenticationSetOrgWorkerOwnerExistsAction
    | APIAuthenticationSetUserPlanExpired

const initialState: APIAuthenticationState = {
    apiKeys: [],
    user: null,
    ownerExists: {},
    orgMemberOwnerExists: {},
    workerOwnerExists: {},
    freemiumPlanExpiryDays: undefined
}

export const APIAuthenticationReducer: Reducer<
    APIAuthenticationState,
    APIAuthenticationAction | AuthAction | BillingAction
> = (state = initialState, action): APIAuthenticationState => {
    switch (action.type) {
        case APIAuthenticationType.setAPIKeys:
            return {
                ...state,
                apiKeys: action.payload
            }
        case APIAuthenticationType.setUserInfo:
            return {
                ...state,
                user: action.payload || null,
                freemiumPlanExpiryDays: calculateFreemiumPlanExpiry(
                    action.payload
                )
            }

        case APIAuthenticationType.setCollaborationOwnerExists:
            return {
                ...state,
                ownerExists: {
                    [action.meta.collaborationId]: action.payload
                }
            }
        case APIAuthenticationType.setOrgMemberOwnerExists:
            return {
                ...state,
                orgMemberOwnerExists: {
                    [action.meta.memberId]: action.payload
                }
            }
        case APIAuthenticationType.setWorkerOwnerExists:
            return {
                ...state,
                workerOwnerExists: {
                    [action.meta.workerId]: action.payload
                }
            }
        case APIAuthenticationType.setFreemiumPlanExpiryDays:
            return { ...state, freemiumPlanExpiryDays: action.payload }
        case AuthUserType.signedOut:
            // `ownerExists` comes from a public endpoint and safe to keep in store.
            return { ...initialState, ownerExists: state.ownerExists }

        default:
            return state
    }
}
